import Http from '@/services/http.service';

/**
 * useAssistant — API del asistente de carga (genérico por `module`).
 *
 * Borradores: la persona sube un documento, el backend lo lee y arma un
 * BORRADOR; si le falta algo, pregunta. El front no decide nada de negocio:
 * muestra `proposal.question`, manda la respuesta y repite hasta que
 * `proposal.missing` quede vacío. Nada se guarda hasta `confirm`.
 * Chat: dudas de la plataforma (cualquier rol, sin plan).
 *
 * Contrato: `monitor-assistant-fe-contract.md` (GME-Api).
 *
 *   POST  /api/assistant/drafts                 subir y leer (multipart `file` + `module`)
 *   GET   /api/assistant/drafts?module&status   lista liviana
 *   GET   /api/assistant/drafts/:id             detalle + opciones + file_sas_url
 *   PATCH /api/assistant/drafts/:id             responder / corregir
 *   POST  /api/assistant/drafts/:id/confirm     Cargar
 *   POST  /api/assistant/drafts/:id/discard     Descartar
 *   POST  /api/assistant/chat                   mensaje ({ message, module?, conversationId? })
 *   GET   /api/assistant/chat/:conversationId   historial (solo las propias)
 */

const DRAFTS = '/api/assistant/drafts';
const CHAT = '/api/assistant/chat';

// Tipos y tamaños que acepta el backend (§ 2).
export const DOCUMENT_ACCEPT = [
  '.jpg', '.jpeg', '.png', '.webp', '.pdf', '.xml', '.xlsx', '.csv',
  'image/jpeg', 'image/png', 'image/webp', 'application/pdf', 'text/xml', 'application/xml', 'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
].join(',');
// .xls (Excel 97-2003) no se puede leer: hay que guardarlo como .xlsx o .csv (§ 2).
export const DOCUMENT_EXT = /\.(jpe?g|png|webp|pdf|xml|xlsx|csv)$/i;
export const SPREADSHEET_EXT = /\.(xlsx|csv)$/i;
export const DOCUMENT_MAX_IMAGE = 5 * 1024 * 1024;
export const DOCUMENT_MAX_FILE = 10 * 1024 * 1024;
export const CHAT_MAX_LENGTH = 2000;

// Avisos que piden confirmación explícita antes de Cargar (§ 6).
export const BLOCKING_WARNINGS = new Set(['duplicate_document', 'possible_duplicate']);

// http.service devuelve err.response en los fallos (no rechaza): se normaliza a
// { ok, status, code, message, data } y la vista decide qué mostrar.
const wrap = (res) => {
  const body = res?.data || {};
  const ok = res?.status >= 200 && res?.status < 300 && body.success !== false;
  return { ok, status: res?.status, code: body.code, message: body.message || body.msg || '', data: body.data ?? null };
};

// El borrador llega como `data.draft` (o `data` directo en algunas respuestas).
export const draftOf = (res) => res?.data?.draft || res?.data || null;

export function useAssistant() {
  // `kind` es opcional: si la pantalla ya sabe qué se sube, la lectura es más rápida.
  const createDraft = async (file, module, kind) => {
    const form = new FormData();
    form.append('file', file);
    form.append('module', module);
    if (kind) form.append('kind', kind);
    return wrap(await Http.post(DRAFTS, form, { headers: { 'Content-Type': 'multipart/form-data' } }));
  };

  const listDrafts = async ({ module, status = 'pending', page = 1, limit = 20 } = {}) =>
    wrap(await Http.get(DRAFTS, { params: { module, status, page, limit } }));

  const getDraft = async (id) => wrap(await Http.get(`${DRAFTS}/${id}`));
  const updateDraft = async (id, changes) => wrap(await Http.patch(`${DRAFTS}/${id}`, changes));
  const confirmDraft = async (id, changes = {}) => wrap(await Http.post(`${DRAFTS}/${id}/confirm`, changes));
  const discardDraft = async (id) => wrap(await Http.post(`${DRAFTS}/${id}/discard`));

  // Siempre usar el conversationId que vuelve: si la conversación caducó (24 h sin
  // actividad) el backend abre otra y responde `restarted: 'expired'`.
  const sendChat = async ({ message, module, conversationId }) =>
    wrap(await Http.post(CHAT, { message, ...(module ? { module } : {}), ...(conversationId ? { conversationId } : {}) }));
  const getConversation = async (conversationId) => wrap(await Http.get(`${CHAT}/${conversationId}`));

  return { createDraft, listDrafts, getDraft, updateDraft, confirmDraft, discardDraft, sendChat, getConversation };
}
